import { useState } from 'react'
import { AlertCircle, LogOut } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import DocumentAcceptance, { DocumentAcceptanceData } from './DocumentAcceptance'
import { api } from '../../lib/api'
import { useToastStore } from '../../store/toastStore'
import { useLogout } from '../../hooks/shared/auth'

interface DocumentAcceptanceModalProps {
  isOpen: boolean
  missingDocuments: string[]
  onAccepted: () => void
}

export default function DocumentAcceptanceModal({
  isOpen,
  missingDocuments,
  onAccepted
}: DocumentAcceptanceModalProps) {
  const [acceptances, setAcceptances] = useState<DocumentAcceptanceData[]>([])
  const [allAccepted, setAllAccepted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const { addToast } = useToastStore()
  const logoutMutation = useLogout()
  const { t } = useTranslation(['auth', 'common'])

  if (!isOpen) return null

  const handleAcceptanceChange = (newAcceptances: DocumentAcceptanceData[], isAllAccepted: boolean) => {
    setAcceptances(newAcceptances)
    setAllAccepted(isAllAccepted)
  }

  const handleSubmit = async () => {
    if (!allAccepted) {
      addToast(t('auth:documentAcceptance.modal.mustAcceptAllToast'), 'error')
      return
    }

    setSubmitting(true)

    try {
      await api.post('/documents/accept-batch', {
        acceptances: acceptances
      })

      addToast(t('auth:documentAcceptance.modal.acceptedToast'), 'success')
      onAccepted()
    } catch (error: any) {
      addToast(
        error.response?.data?.detail || t('auth:documentAcceptance.modal.acceptErrorToast'),
        'error'
      )
    } finally {
      setSubmitting(false)
    }
  }

  const handleLogout = () => {
    logoutMutation.mutate()
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop - non-dismissable */}
      <div className="fixed inset-0 bg-black bg-opacity-75 transition-opacity" />

      {/* Modal */}
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="relative w-full max-w-2xl bg-white rounded-lg shadow-xl">
          {/* Header */}
          <div className="p-6 border-b">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0">
                <AlertCircle className="h-8 w-8 text-blue-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">
                  {t('auth:documentAcceptance.modal.title')}
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  {t('auth:documentAcceptance.modal.subtitle')}
                </p>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="p-6">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-yellow-800">
                <strong>{t('auth:documentAcceptance.modal.warningLabel')}</strong>{' '}
                {t('auth:documentAcceptance.modal.warningText')}
              </p>
            </div>

            <DocumentAcceptance
              onChange={handleAcceptanceChange}
              disabled={submitting}
            />
          </div>

          {/* Footer */}
          <div className="flex justify-between items-center gap-3 p-6 border-t bg-gray-50">
            <button
              onClick={handleLogout}
              disabled={submitting}
              className="px-4 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              <LogOut className="h-4 w-4" />
              <span>{t('common:nav.logout')}</span>
            </button>
            
            <button
              onClick={handleSubmit}
              disabled={!allAccepted || submitting}
              className="px-6 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              {submitting ? (
                <div className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>{t('common:saving')}</span>
                </div>
              ) : (
                t('auth:documentAcceptance.modal.submitButton')
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
